import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { signalRunStarted } from '../work/agent-work.store';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { putBrief } from '../../../common/services/content-brief.service';
import { LiveTurn, reduceLive, startTurn } from './live-turn';
import { useProductsApi } from '../../../common/hooks/use-products-api.hook';

import {
    AssistantMessage,
    AssistantThread,
    PendingAction,
    ThreadSummary,
    useAssistantApi,
} from '../../../common/hooks/use-assistant-api.hook';

/**
 * The assistant follows the vendor around.
 *
 * It is mounted once, beside the app rather than inside a page, because "ask
 * about this" is a thing you want on the page you are already on — closing the
 * catalogue to go and ask a question about the catalogue is the behaviour this
 * exists to avoid.
 *
 * Two sizes, not two features. Docked is a column beside the work, for a
 * question with a short answer. Expanded is the whole window, for a
 * conversation you are going to stay in. The same thread is underneath both,
 * so expanding never loses what was said.
 */

export type AskAiSize = 'closed' | 'docked' | 'expanded';

/** An uploaded image, with the local name so the vendor recognises it. */
export interface Attachment {
    url: string;
    name: string;
}

interface AskAiState {
    size: AskAiSize;
    /** The conversation on screen. Empty until something is asked. */
    messages: AssistantMessage[];
    /** Changes the assistant proposed in this conversation. */
    actions: PendingAction[];
    /** The vendor's other conversations, for the session list. */
    threads: ThreadSummary[];
    threadUuid: string | null;
    /** Which tool is running right now, if any. */
    activity: string | null;
    /**
     * Everything the turn in progress has done so far — the lead's steps and
     * the team it sent work to. Null between turns.
     */
    live: LiveTurn | null;
    /**
     * Start a new conversation with this question, wherever the vendor is.
     * Unlike `ask`, it does not open the side panel: Home shows the
     * conversation as a page of its own.
     */
    startChat(question: string): Promise<void>;
    sending: boolean;
    error: string | null;
    /** Images attached to the message being written, already uploaded. */
    attachments: Attachment[];
    /** The vendor's products, for naming one with `@` in the composer. */
    mentionable: Array<{ uuid: string; name: string; sku?: string }>;
    attach(file: File): Promise<void>;
    removeAttachment(url: string): void;
    uploading: boolean;
    send(override?: string): Promise<void>;
    newChat(): void;
    openThread(uuid: string): Promise<void>;
    /** Delete a conversation. Deleting the one on screen leaves an empty panel. */
    removeThread(uuid: string): Promise<void>;
    refreshThreads(): Promise<void>;
    /** `edits` carries whatever the vendor changed in the proposal's form. */
    apply(actionId: string, edits?: Record<string, unknown>): Promise<void>;
    decline(actionId: string): Promise<void>;
    /** Text placed in the composer but not sent — the vendor still presses send. */
    draft: string;
    open(draft?: string): void;
    /** Open the panel and send this question at once. */
    ask(question: string): void;
    expand(): void;
    collapse(): void;
    close(): void;
    setDraft(value: string): void;
    /** Where the vendor was when they opened it, so the assistant can say something useful. */
    context: string | null;
    setContext(value: string | null): void;
}

const AskAiContext = createContext<AskAiState | null>(null);

export const AskAiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const api = useAssistantApi();
    const navigate = useNavigate();
    /*
     * Where the vendor is, sent with each question: "what should I change
     * here?" asked from the Ads page is about ads, and the server routes the
     * turn's tools by it. Read through a ref so a navigation does not rebuild
     * `send`.
     */
    const location = useLocation();
    const pageRef = useRef(location.pathname + location.search);
    pageRef.current = location.pathname + location.search;
    const [size, setSize] = useState<AskAiSize>('closed');
    const [draft, setDraft] = useState('');
    const [context, setContext] = useState<string | null>(null);

    const [messages, setMessages] = useState<AssistantMessage[]>([]);
    const [actions, setActions] = useState<PendingAction[]>([]);
    const [threads, setThreads] = useState<ThreadSummary[]>([]);
    const [threadUuid, setThreadUuid] = useState<string | null>(null);
    const [activity, setActivity] = useState<string | null>(null);
    const [live, setLive] = useState<LiveTurn | null>(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [uploading, setUploading] = useState(false);

    /**
     * The catalogue, for naming a product with `@`.
     *
     * Loaded once when the panel first opens rather than on mount: most pages
     * never open it, and this is the vendor's whole catalogue. Failing quietly
     * is right — mentions degrade to typing the name, which the assistant
     * resolves anyway.
     */
    const productsApi = useProductsApi();
    const [mentionable, setMentionable] = useState<Array<{ uuid: string; name: string; sku?: string }>>([]);
    const askedForProducts = useRef(false);

    /** Lets a new question cut off one that is still streaming. */
    const inFlight = useRef<AbortController | null>(null);

    /**
     * Whether the last conversation has been restored, or deliberately given
     * up on. Set by the restore itself, and by anything that is the vendor
     * choosing a different starting point.
     */
    const restoredLatest = useRef(false);

    const refreshThreads = useCallback(async () => {
        try {
            setThreads(await api.listThreads());
        } catch {
            // The session list is a convenience. Failing to load it should not
            // stop the vendor asking a question.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Re-read the thread from the server rather than patching state by hand.
     *
     * The server is where a message becomes real and where an action's status
     * lives — including when it was resolved in another tab. Reconstructing
     * that locally is how the two drift apart.
     */
    const reload = useCallback(async (uuid: string) => {
        try {
            const thread: AssistantThread = await api.getThread(uuid);
            setMessages(thread.messages ?? []);
            setActions(thread.actions ?? []);
        } catch {
            /* leave what is on screen */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const attach = useCallback(async (file: File) => {
        setUploading(true);
        setError(null);

        try {
            const { url } = await api.uploadImage(file);
            setAttachments((prev) => [...prev, { url, name: file.name }]);
        } catch (err: any) {
            setError(err?.message ?? 'That image could not be uploaded.');
        } finally {
            setUploading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeAttachment = useCallback((url: string) => {
        setAttachments((prev) => prev.filter((a) => a.url !== url));
    }, []);

    /**
     * `override` is for anything that already knows what it is asking.
     *
     * A starter on the dashboard, or a suggestion in the panel, is a question
     * the vendor has just chosen — putting it in the box and waiting for Enter
     * asks them to agree with themselves. Passing the text avoids waiting a
     * render for `draft` state to catch up, which is the reason this could not
     * simply be `setDraft(x); send()`.
     */
    const send = useCallback(async (override?: string, fresh = false) => {
        const message = (override ?? draft).trim();
        // An image on its own is a question too — "what is this?" — so a
        // message is only required when nothing is attached.
        if ((!message && !attachments.length) || sending) return;

        inFlight.current?.abort();
        const controller = new AbortController();
        inFlight.current = controller;

        /*
         * The turn becomes a run on the server, and the dashboard's agent card
         * is not polling — nothing was running a moment ago. Told here rather
         * than where the question was typed, because a question can be asked
         * from the top bar, the card, or the panel itself, and all three end up
         * on this line.
         */
        signalRunStarted();

        // Shown immediately. Waiting for the server to echo it back leaves the
        // vendor looking at an emptied box, unsure it registered.
        setMessages((prev) => [...prev, {
            role: 'vendor',
            text: message,
            // Shown straight away rather than after the reload, so the question
            // and the picture it is about appear together.
            ...(attachments.length ? { images: attachments.map((a) => a.url) } : {}),
            at: new Date().toISOString(),
        }]);
        setDraft('');
        // Cleared with the message they belong to: an attachment left behind
        // would silently ride along with the next question.
        setAttachments([]);
        setSending(true);
        setError(null);
        setActivity(null);
        setLive(startTurn());

        // A fresh start ignores whatever thread was on screen; the state that
        // says so may not have caught up with the reset yet.
        let uuid = fresh ? null : threadUuid;

        try {
            await api.ask(
                { threadUuid: uuid ?? undefined, message, images: attachments.map((a) => a.url), page: pageRef.current },
                (event) => {
                    setLive((turn) => (turn ? reduceLive(turn, event) : turn));
                    if (event.type === 'thinking' && event.data?.threadUuid) {
                        uuid = event.data.threadUuid;
                        setThreadUuid(uuid);
                    }
                    if (event.type === 'tool_call') setActivity(event.data?.tool ?? null);
                    if (event.type === 'error') {
                        // Running out of credits is a normal, actionable state
                        // rather than a fault, and the server's sentence is
                        // English. The code is what to key off; the message is
                        // the fallback for everything else.
                        setError(
                            event.data?.code === 'insufficient_credits'
                                ? (t('ASSISTANT.OUT_OF_CREDITS') as string)
                                : event.data?.message ?? (t('ASSISTANT.ERROR', 'Something went wrong.') as string),
                        );
                    }
                },
                controller.signal,
            );
        } catch (err: any) {
            if (err?.name !== 'AbortError') setError(err?.message ?? 'The assistant could not be reached.');
        } finally {
            setSending(false);
            setActivity(null);
            inFlight.current = null;
        }

        if (uuid) {
            await reload(uuid);
            await refreshThreads();
        }
        // After the reload, so the saved reply replaces the live one in a
        // single paint rather than the work vanishing before the answer lands.
        setLive(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft, sending, attachments, threadUuid, reload, refreshThreads]);

    const openThread = useCallback(
        async (uuid: string) => {
            inFlight.current?.abort();
            setThreadUuid(uuid);
            setError(null);
            await reload(uuid);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [reload],
    );

    /**
     * Delete a conversation.
     *
     * Deleting the one on screen empties the panel rather than opening
     * whichever thread happens to be next: the vendor just removed what they
     * were looking at, and landing them in an unrelated conversation reads as
     * the wrong one having been deleted.
     */
    const removeThread = useCallback(
        async (uuid: string) => {
            try {
                await api.removeThread(uuid);
            } catch (err: any) {
                setError(err?.response?.data?.message ?? err?.message ?? 'That conversation could not be deleted.');
                return;
            }

            setThreads((prev) => prev.filter((th) => th.uuid !== uuid));

            if (uuid === threadUuid) {
                inFlight.current?.abort();
                // Nothing to restore over: they chose to be rid of it.
                restoredLatest.current = true;
                setThreadUuid(null);
                setMessages([]);
                setActions([]);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [threadUuid],
    );

    const newChat = useCallback(() => {
        // Their explicit choice of an empty panel, which no restore may undo.
        restoredLatest.current = true;
        inFlight.current?.abort();
        setThreadUuid(null);
        setMessages([]);
        setActions([]);
        setDraft('');
        setAttachments([]);
        setError(null);
        setLive(null);
    }, []);

    const resolve = useCallback(
        async (actionId: string, how: 'apply' | 'decline', edits: Record<string, unknown> = {}) => {
            if (!threadUuid) return;

            try {
                if (how === 'apply') {
                    const applied = await api.applyAction(threadUuid, actionId, edits);

                    /**
                     * Some changes finish here; a new product does not. When the
                     * server says where the work continues, go — and step the
                     * panel down to docked rather than closing it, so the
                     * conversation is still beside them while they finish.
                     */
                    if (applied?.next?.route) {
                        /**
                         * Put the brief down before navigating.
                         *
                         * The studio reads it on mount, so it has to be there
                         * first — otherwise the vendor arrives at exactly the
                         * empty canvas this exists to prevent.
                         */
                        const handoff = applied.next.handoff;
                        if (handoff?.brief) {
                            putBrief(handoff.brief, handoff.productUuid, handoff.referenceUrl);
                        }

                        setSize((current) => (current === 'expanded' ? 'docked' : current));
                        navigate(applied.next.route);
                    }
                } else {
                    await api.declineAction(threadUuid, actionId);
                }
            } catch (err: any) {
                setError(err?.response?.data?.message ?? err?.message ?? 'That change could not be completed.');
            }

            // Reloaded either way: the server decides what state an action ended
            // in, and it may have been resolved somewhere else.
            await reload(threadUuid);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [threadUuid, reload, navigate],
    );

    /**
     * Opening with a draft is how the rest of the app hands work over — the
     * same idea as an insight card briefing Content Studio. Never auto-sent:
     * the vendor reads it, edits if they want, and sends.
     */
    const open = useCallback((withDraft?: string) => {
        if (withDraft !== undefined) setDraft(withDraft);
        setSize((current) => (current === 'expanded' ? current : 'docked'));
    }, []);

    /*
     * `ask` reaches the current `send` through a ref rather than a dependency.
     * Depending on it would rebuild `ask` — and the memoised context value with
     * it — on every keystroke, since `send` closes over the draft.
     */
    const sendRef = useRef<((override?: string, fresh?: boolean) => Promise<void>) | null>(null);
    sendRef.current = send;

    const startChat = useCallback(async (question: string) => {
        restoredLatest.current = true;
        inFlight.current?.abort();
        setThreadUuid(null);
        setMessages([]);
        setActions([]);
        setError(null);
        await sendRef.current?.(question, true);
    }, []);

    /** Open and ask, in one gesture — for a question the vendor already chose. */
    const ask = useCallback(
        (question: string) => {
            // A chosen question starts where the vendor is, not in whatever
            // they were last talking about.
            restoredLatest.current = true;
            setSize((current) => (current === 'expanded' ? current : 'docked'));
            void sendRef.current?.(question);
        },
        [],
    );

    /**
     * Reopen the conversation they were last in.
     *
     * The provider is mounted beside the app, so a thread survives closing and
     * reopening the panel on its own — what it did not survive was a page load,
     * and the vendor came back to an empty panel with their last conversation
     * one menu away. Restored on the first open after a load rather than on
     * mount, because most pages never open the panel at all and this is a
     * request.
     *
     * Once only, and never against the vendor's own choice: starting a new chat
     * or asking a chosen question both mark the restore as spent, so neither
     * gets quietly replaced by an older conversation.
     */
    useEffect(() => {
        if (size === 'closed' || restoredLatest.current) return;
        // Anything already on screen is a conversation in progress, and
        // replacing it would be the bug this is meant to prevent.
        if (threadUuid || messages.length || sending) return;

        restoredLatest.current = true;
        let cancelled = false;

        (async () => {
            try {
                const all = await api.listThreads();
                const latest = all?.[0];
                if (cancelled || !latest?.uuid) return;

                setThreads(all);
                setThreadUuid(latest.uuid);
                await reload(latest.uuid);
            } catch {
                // An empty panel is a fine place to start; failing to restore
                // is not worth an error in front of a question.
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size]);

    // Fetched the first time the panel is opened, and never again.
    useEffect(() => {
        if (size === 'closed' || askedForProducts.current) return;
        askedForProducts.current = true;
        let cancelled = false;
        (async () => {
            try {
                const all = await productsApi.findAllProducts();
                if (cancelled) return;
                setMentionable((all || [])
                    .filter((p: any) => p?.uuid && p?.name)
                    .map((p: any) => ({ uuid: p.uuid, name: p.name, sku: p.sku })));
            } catch {
                // Mentions degrade to typing the name, which still works.
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size]);

    const value = useMemo<AskAiState>(
        () => ({
            size,
            draft,
            context,
            messages,
            actions,
            threads,
            threadUuid,
            activity,
            live,
            startChat,
            sending,
            error,
            attachments,
            mentionable,
            attach,
            removeAttachment,
            uploading,
            send,
            newChat,
            openThread,
            removeThread,
            refreshThreads,
            apply: (id: string, edits?: Record<string, unknown>) => resolve(id, 'apply', edits ?? {}),
            decline: (id: string) => resolve(id, 'decline'),
            open,
            ask,
            expand: () => setSize('expanded'),
            collapse: () => setSize('docked'),
            close: () => setSize('closed'),
            setDraft,
            setContext,
        }),
        [size, draft, context, messages, actions, threads, threadUuid, activity, live, startChat, sending, error, attachments, mentionable, attach, removeAttachment, uploading, send, newChat, openThread, removeThread, refreshThreads, resolve, open, ask],
    );

    return <AskAiContext.Provider value={value}>{children}</AskAiContext.Provider>;
};

/**
 * Returns null rather than throwing when the provider is absent.
 *
 * The top bar renders in the super-admin tree as well, where the assistant is
 * not mounted — a hook that threw would take the whole page down over a button
 * that simply should not be there.
 */
export const useAskAi = (): AskAiState | null => useContext(AskAiContext);
